import React, {FormEvent, useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {
	Breadcrumbs,
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Stack,
	TextField, Typography
} from "@mui/material";
import {GraphQlDataGrid} from "../../../components/common/grid/GraphQlDataGrid";
import {graphQLServices} from "../../../services/GraphQLServices";
import {formatDateTimeShort, GlobalState} from "../../../global/GlobalState";
import {Link} from "react-router-dom";
import {textColumn, tooltipColumn} from "../../../components/common/grid/Columns";
import {GridColDef, GridPagination} from "@mui/x-data-grid";
import {GET_QUESTION_DEFINITIONS, GET_QUESTIONS_OVERVIEW} from "./Queries";
import {openQuestionDetailsForNewDefinition} from "./DynamicFormsQuestionDetails";
import {dynamicFormsDefaultTargetStage} from "../DynamicFormsOverviewPage";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {translateFunction} from "../../../localization/TranslationService";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";

const translate = translateFunction("SureBasePortalDynamicForms");

function DynamicFormsQuestionsOverview() {
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Link"),
			renderCell: (params) => (
				<Link to={`./${encodeURIComponent(params.row.definition)}`} style={{
					textDecoration: "initial"
				}}>
					<Button
						variant="contained"
						size="small"
						tabIndex={-1}
					>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
		},
		{
			field: "definition",
			headerName: translate("Definition"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "clientName",
			headerName: translate("Client Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "frontEndName",
			headerName: translate("Front-End Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			type: "dateTime",
			minWidth: 220,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		}
	];
	
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={dynamicFormsDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/dynamicforms">Dynamic Forms</Link>
				<Typography color="text.primary">Questions</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<GraphQlDataGrid
					rows={[]}
					query={GET_QUESTIONS_OVERVIEW}
					graphFunction={"questionSetups"}
					client={graphQLServices.dynamicFormsClient}
					rowsPerPageOptions={[50, 100]}
					initialPageSize={50}
					columns={columns}
					getRowId={row => row.fullId}
					defaultSortModel={[
						{
							field: "definition",
							sort: "asc"
						}
					]}
					slots={{
						footer: CustomFooter
					}}
					queryVariables={{stage: GlobalState.targetKiziStage(dynamicFormsDefaultTargetStage)}}
				/>
			</Paper>}
		/>
	</>;
}

function CustomFooter(props: any) {
	const [open, setOpen] = useState(false);
	// existing definitions are used to determine if the newly created definition would actually be new
	const [existingDefinitions, setExistingDefinitions] = useState<Set<string>>();
	const [error, setError] = useState("");
	
	async function fetchDefinitions() {
		const response = await graphQLServices.dynamicFormsClient.query({
			query: GET_QUESTION_DEFINITIONS,
			fetchPolicy: "no-cache",
			variables: {
				stage: GlobalState.targetKiziStage(dynamicFormsDefaultTargetStage),
			}
		});
		
		const allNames = response.data.questionSetups.nodes.map((it: any) => it.definition);
		
		const set = new Set<string>();
		allNames.forEach((it: any) => set.add(it));
		setExistingDefinitions(set);
	}
	
	const handleClickOpen = () => {
		setOpen(true);
		setError("");
		
		if (!existingDefinitions)
			fetchDefinitions();
	};
	
	const handleClose = () => {
		setOpen(false);
	};
	
	const handleCreate = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		
		const data = new FormData(event.target as HTMLFormElement);
		const definition = data.get("definition") as string;
		if (existingDefinitions && existingDefinitions.has(definition)) {
			setError("This definition already exists.");
		} else {
			setOpen(false);
			openQuestionDetailsForNewDefinition(definition);
		}
	};
	
	return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} style={{
		padding: "0 10px"
	}}>
		<Button
			variant={"contained"}
			onClick={handleClickOpen}
		>{translate("Add")}</Button>
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>{translate("New Question")}</DialogTitle>
			<DialogContent>
				<DialogContentText>
					{translate("Please provide the definition identifier to create a question.")}
				</DialogContentText>
				<form onSubmit={handleCreate}>
					<TextField
						name={"definition"}
						error={!!error}
						helperText={error ? translate(error) : "​"}
						autoFocus
						margin="dense"
						label={translate("Definition")}
						fullWidth
						variant="standard"
						required={true}
						inputProps={{
							pattern: "^[a-zA-Z0-9.$\\[\\]_-]+$",
						}}
						onChange={event => {
							if (existingDefinitions) {
								if (existingDefinitions.has(event.target.value)) {
									setError("This definition already exists.");
								} else {
									setError("");
								}
							}
						}}
					/>
					<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
						<Button onClick={handleClose}>{translate("Cancel")}</Button>
						<Button type={"submit"} color={"success"}>{translate("Create")}</Button>
					</Stack>
				</form>
			</DialogContent>
		</Dialog>
		<GridPagination {...props} labelRowsPerPage={translate("Rows per page:")}/>
	</Stack>;
}

export const DynamicFormsQuestionsOverviewPage = pageAuthorizedByGroup(DynamicFormsQuestionsOverview, ["Admin", "DynamicForms"]);
